import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { hrApi } from '../../../services/hrApi';
import { interviewApi } from '../../../services/interViewApi';

import { InterviewState } from './types';

const initialState: InterviewState = {
  currentQuestionIndex: 0,
  isPlaying: false,
  isAnswering: false,
  answerTimeLeft: 30,
  currentAudioTime: 0,
  audioDuration: 0,
  isCompleted: false,
  isRecording: false,
  micPermission: null,
  recordedAudios: [],
  languages: [],
  interviewCompanyNamesIds: [],
  selectedInterviewCompany: {
    companyName: '',
    id: ''
  },
  driveData: {},
  hrData: {
    id: '',
    isActive: false,
    createdDate: '',
    updatedDate: null,
    userId: '',
    isTestCompleted: false,
    testTDO: {
      id: '',
      isActive: false,
      createdDate: '',
      updatedDate: null,
      questionIds: null,
      topic: '',
      questions: [],
      additionalProps: null,
      testName: ''
    }
  },
  testData: {
    id: '',
    isActive: false,
    createdDate: '',
    updatedDate: null,
    userId: '',
    isTestCompleted: false,
    testSet: {
      id: '',
      isActive: false,
      createdDate: '',
      updatedDate: null,
      questionIds: null,
      topic: '',
      questions: [],
      additionalProps: null,
      testName: ''
    },
    answers: [
      {
        questionId: '',
        uploadedAnswerFile: {
          fileName: '',
          folderPath: '',
          timeStamp: '',
          bucketName: '',
          originalFileName: '',
          region: ''
        },
        aiQuestionEvaluation: {
          userAnswerTranscript: '',
          isRelavent: false,
          suggestion: ''
        },
        question: '',
        idealAnswer: ''
      }
    ],
    overAllEvaluationResult: {
      overallLevel: '',
      overallTitle: '',
      overallInterpretation: '',
      overallFeedback: '',
      clarityofExpressionState: '',
      technicalDepthState: '',
      practicalExperienceState: '',
      problemSolvingApproachState: '',
      professionalismState: ''
    },
    additionalProps: null
  },
  avatars: [],
  currentStep: 'language',
  difficultyLevel: ''
};

const interviewSlice = createSlice({
  name: 'interview',
  initialState,
  reducers: {
    setDifficultyLevel: (state, action) => {
      state.difficultyLevel = action.payload;
    },
    setSelectedInterviewCompany: (state, action) => {
      state.selectedInterviewCompany = action.payload;
    },
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
    },
    setInterviewCompanyNamesIds: (state, action) => {
      state.interviewCompanyNamesIds = action.payload;
    },
    setPlaying: (state, action: PayloadAction<boolean>) => {
      state.isPlaying = action.payload;
    },
    setAnswering: (state, action: PayloadAction<boolean>) => {
      state.isAnswering = action.payload;
    },
    setAnswerTimeLeft: (state, action: PayloadAction<number>) => {
      state.answerTimeLeft = action.payload;
    },
    setCurrentAudioTime: (state, action: PayloadAction<number>) => {
      state.currentAudioTime = action.payload;
    },
    setAudioDuration: (state, action: PayloadAction<number>) => {
      state.audioDuration = action.payload;
    },
    setResetInterviewSlice: state => {
      state = initialState;
    },
    nextQuestion: state => {
      if (
        state.currentQuestionIndex <
        state.testData?.testSet?.questions.length - 1
      ) {
        state.currentQuestionIndex += 1;
        state.isAnswering = false;
        state.answerTimeLeft = 30;
        state.currentAudioTime = 0;
        state.audioDuration = 0;
        state.isRecording = false;
      } else {
        state.isCompleted = true;
      }
    },
    resetInterview: state => {
      state.currentQuestionIndex = 0;
      state.isPlaying = false;
      state.isAnswering = false;
      state.answerTimeLeft = 30;
      state.currentAudioTime = 0;
      state.audioDuration = 0;
      state.isCompleted = false;
      state.isRecording = false;
    },
    setRecording: (state, action: PayloadAction<boolean>) => {
      state.isRecording = action.payload;
    },
    setMicPermission: (
      state,
      action: PayloadAction<'requesting' | 'granted' | 'denied' | null>
    ) => {
      state.micPermission = action.payload;
    },
    addRecordedAudio: (state, action: PayloadAction<Blob>) => {
      state.recordedAudios.push(action.payload);
    },
    setTestData: (state, action) => {
      state.testData = action.payload;
    },
    setDriveData: (state, action) => {
      state.driveData = action.payload;
    },
    setHrData: (state, action) => {
      state.hrData = action.payload;
    }
  },
  extraReducers: builder => {
    builder.addMatcher(
      interviewApi.endpoints.getTopic.matchFulfilled,
      (state, action) => {
        state.languages = action.payload.data;
      }
    );
    builder.addMatcher(
      interviewApi.endpoints.generateTest.matchFulfilled,
      (state, action) => {
        state.testData = action.payload.data;
      }
    );
    builder.addMatcher(
      hrApi.endpoints.hrGenerateTest.matchFulfilled,
      (state, action) => {
        const updatedData = {
          ...action.payload.data,
          testDTO: {
            ...action.payload.data.testSet,
            questions: action.payload.data.testSet.questions.map(
              ({
                questionText,
                ...rest
              }: {
                questionText: string;
                [key: string]: any;
              }): { transcript: string; [key: string]: any } => ({
                ...rest,
                transcript: questionText
              })
            )
          }
        };
        state.testData = updatedData;
      }
    );
    builder.addMatcher(
      interviewApi.endpoints.getAvatars.matchFulfilled,
      (state, action) => {
        state.avatars = action.payload.data;
      }
    );
  }
});

export const {
  setPlaying,
  setAnswering,
  setAnswerTimeLeft,
  setCurrentAudioTime,
  setAudioDuration,
  nextQuestion,
  resetInterview,
  setRecording,
  setMicPermission,
  addRecordedAudio,
  setCurrentStep,
  setDifficultyLevel,
  setSelectedInterviewCompany,
  setInterviewCompanyNamesIds,
  setTestData,
  setHrData,
  setDriveData,
  setResetInterviewSlice
} = interviewSlice.actions;

export default interviewSlice.reducer;
